/**
 * PaymentMethodsSection — Profile settings section for managing saved cards.
 *
 * Lists user's saved cards with brand, last4, expiry, default badge.
 * Lets user add a card (via Stripe SetupIntent + PaymentElement modal),
 * set a card as default, and remove a card.
 */
import { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Plus, Loader2, Star, Trash2, X, AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from '../lib/toast';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Stable across renders — Stripe warns if the options.appearance object identity changes
const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#1E3A5F',
    fontFamily: 'Manrope, system-ui, sans-serif',
    borderRadius: '8px',
  },
};

const BRAND_LABEL = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  unknown: 'Card',
};

function brandLabel(brand) {
  return BRAND_LABEL[brand?.toLowerCase()] || (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card');
}

function formatExpiry(month, year) {
  const m = String(month).padStart(2, '0');
  const y = String(year).slice(-2);
  return `${m}/${y}`;
}

export function PaymentMethodsSection() {
  const { api } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchCards = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/payment-methods');
      setCards(res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const handleSetDefault = async (pmId) => {
    setActingId(pmId);
    try {
      await api.post(`/payment-methods/${pmId}/default`);
      toast.success('Default card updated');
      await fetchCards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set default card');
    } finally {
      setActingId(null);
    }
  };

  const handleRemove = async (pmId) => {
    if (!window.confirm('Remove this card from your account?')) return;
    setActingId(pmId);
    try {
      await api.delete(`/payment-methods/${pmId}`);
      toast.success('Card removed');
      await fetchCards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove card');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--theme-text-primary)]">Payment Methods</h2>
          <p className="text-sm text-[var(--theme-text-muted)] mt-0.5">
            Cards you've saved are used for security holds and WANTD+ subscriptions.
          </p>
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          className="bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white h-9"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Add Card
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--theme-text-muted)]" />
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && !error && cards.length === 0 && (
        <div className="rounded-xl border border-dashed border-[var(--theme-border)] p-8 text-center">
          <CreditCard className="w-8 h-8 text-[var(--theme-text-muted)] mx-auto mb-2" />
          <p className="text-sm text-[var(--theme-text-secondary)]">
            No saved cards yet
          </p>
          <p className="text-xs text-[var(--theme-text-muted)] mt-1">
            Add a card to skip card entry on future security holds.
          </p>
        </div>
      )}

      {!loading && !error && cards.length > 0 && (
        <div className="space-y-2">
          {cards.map(card => (
            <div
              key={card.id}
              className="flex items-center justify-between p-3 rounded-xl border border-[var(--theme-border)] bg-[var(--theme-card)]"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-[var(--theme-hover)] flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--theme-text-primary)]">
                      {brandLabel(card.brand)} •••• {card.last4}
                    </span>
                    {card.is_default && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <Star className="w-2.5 h-2.5" />
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--theme-text-muted)] mt-0.5">
                    Expires {formatExpiry(card.exp_month, card.exp_year)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {!card.is_default && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSetDefault(card.id)}
                    disabled={!!actingId}
                    className="h-8 text-xs"
                  >
                    {actingId === card.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      'Set Default'
                    )}
                  </Button>
                )}
                <button
                  onClick={() => handleRemove(card.id)}
                  disabled={!!actingId}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 text-[var(--theme-text-muted)] hover:text-red-600 transition-colors disabled:opacity-50"
                  aria-label="Remove card"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddCardModal
          onClose={() => setShowAdd(false)}
          onAdded={async () => {
            setShowAdd(false);
            await fetchCards();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AddCardModal — Stripe SetupIntent + PaymentElement
// ─────────────────────────────────────────────────────────────────────────────

function AddCardModal({ onClose, onAdded }) {
  const { api } = useAuth();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.post('/payment-methods/setup-intent');
        setClientSecret(res.data.client_secret);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to start card setup');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const elementsOptions = clientSecret
    ? { clientSecret, appearance: STRIPE_APPEARANCE }
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[var(--theme-border)]">
          <h3 className="text-lg font-semibold text-[var(--theme-text-primary)]">Add a card</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-muted)] transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[var(--theme-text-muted)]" />
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {!loading && !error && clientSecret && elementsOptions && (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <AddCardForm onClose={onClose} onAdded={onAdded} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
}

function AddCardForm({ onClose, onAdded }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError('');

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'Failed to save card');
      setSubmitting(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      toast.success('Card saved');
      await onAdded();
    } else {
      setError(`Unexpected status: ${setupIntent?.status}. Please try again.`);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ wallets: { link: 'never', applePay: 'never', googlePay: 'never' } }} />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-1.5 text-xs text-[var(--theme-text-muted)]">
        <ShieldCheck className="w-3.5 h-3.5" />
        Your card is securely stored by Stripe — WANTD never sees your card details.
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-10"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || submitting}
          className="flex-1 h-10 bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Save Card'
          )}
        </Button>
      </div>
    </form>
  );
}
